/**
 * The single MUI theme for the auditor dashboard UI, built on MUI's
 * colorSchemes + CSS variables API (createTheme({ cssVariables: true, ... })).
 * Surfaces are NEUTRAL in both schemes -- no navy wall -- and the 1NL brand
 * colours are demoted to functional accents:
 *
 *   - BLUE (primary) = interactive / sales / the one contained CTA per view.
 *   - YELLOW (secondary) = payout accent only (e.g. the dashboard money
 *     chart); it is NOT a button colour -- owner ruling 2026-07-14 killed
 *     yellow buttons and top-edge accent strips entirely.
 *   - RED = destructive only (outlined on row actions; contained only as
 *     the final confirm button inside ConfirmDialog).
 *
 * `colorSchemeSelector: 'data-mui-color-scheme'` (rather than the createTheme
 * default of 'media') is required so `useColorScheme().setMode(...)` can
 * force a scheme instead of only following the OS preference -- see
 * https://mui.com/material-ui/customization/css-theme-variables/configuration/#toggling-dark-mode-manually
 *
 * Colour tokens are re-exported as `palette` so screens style surfaces from
 * one place instead of importing @mui colors ad hoc. Every value below is a
 * `var(--mui-palette-...)` CSS variable reference (not a JS-computed value),
 * so it resolves to the correct scheme automatically in the browser without
 * screens needing to re-render on toggle.
 */
import { createTheme, type Theme } from '@mui/material/styles';
import { yellow } from '@mui/material/colors';

export const SANS_FONT =
  '"Segoe UI", system-ui, -apple-system, "Helvetica Neue", Arial, sans-serif';

/** localStorage key MUI uses to persist the user's light/dark/system choice. */
export const MODE_STORAGE_KEY = '1nl.auditor.mode';

/** Shared colour tokens -- scheme-aware CSS variable references, named strictly by role. */
export const palette = {
  textPrimary: 'var(--mui-palette-text-primary)',
  textSecondary: 'var(--mui-palette-text-secondary)',
  textMuted: 'var(--mui-palette-text-disabled)',

  quietBorder: 'var(--mui-palette-divider)',
  quietBorderHover: 'var(--mui-palette-action-active)',
  quietHover: 'var(--mui-palette-action-hover)',

  destructive: 'var(--mui-palette-error-main)',
  success: 'var(--mui-palette-success-main)',
  warningAmber: 'var(--mui-palette-warning-main)',
} as const;

/**
 * A strong, consistent focus ring in primary blue (owner ruling 2026-07-14,
 * round 2: yellow demoted further -- it now lives only in the brand logo and
 * the theme's own secondary/warning palette definitions).
 */
export const FOCUS_RING =
  '0 0 0 3px var(--mui-palette-background-default), 0 0 0 6px var(--mui-palette-primary-main)';

/** Money figures always use tabular (fixed-width) digits so columns of numbers align. */
export const MONEY_NUMERIC_SX = { fontVariantNumeric: 'tabular-nums' } as const;

export const theme: Theme = createTheme({
  cssVariables: { colorSchemeSelector: 'data-mui-color-scheme' },
  colorSchemes: {
    light: {
      palette: {
        primary: { main: '#2551b8' },
        secondary: { main: yellow[700], light: yellow[500], contrastText: 'rgba(0,0,0,0.87)' },
        background: { default: '#f4f5f7', paper: '#ffffff' },
        text: { primary: '#191c20', secondary: 'rgba(25,28,32,0.68)' },
        divider: 'rgba(15,23,42,0.12)',
        warning: { main: yellow[700], dark: yellow[800], light: yellow[500], contrastText: 'rgba(0,0,0,0.87)' },
        error: { main: '#e53935' },
        success: { main: '#43a047' },
      },
    },
    dark: {
      palette: {
        // Lighter blue than the light scheme's primary for contrast on a dark ground.
        primary: { main: '#64b5f6', contrastText: 'rgba(0,0,0,0.87)' },
        secondary: { main: yellow[700], light: yellow[500], contrastText: 'rgba(0,0,0,0.87)' },
        // Neutral charcoal -- deliberately NOT blue-tinted.
        background: { default: '#15171b', paper: '#1e2126' },
        text: { primary: '#f0f2f5', secondary: 'rgba(240,242,245,0.70)' },
        divider: 'rgba(240,242,245,0.14)',
        warning: { main: yellow[700], dark: yellow[800], light: yellow[500], contrastText: 'rgba(0,0,0,0.87)' },
        // Lighter red than the light scheme's error so the small bold delta chips clear WCAG AA (5.4:1) on dark paper.
        // `dark` is pinned LIGHTER than main (not MUI's auto darken(main, 0.3)) because filled Alerts and
        // contained-error button hover both paint on it: only a shade this side of getContrastText's
        // threshold keeps the SAME dark text readable (AA) on main and on dark -- see theme.test.ts.
        error: { main: '#e57373', dark: '#ef9a9a' },
        success: { main: '#43a047' },
      },
    },
  },
  shape: { borderRadius: 10 },
  typography: {
    fontFamily: SANS_FONT,
    fontSize: 15,
    button: { fontWeight: 800, textTransform: 'none', letterSpacing: 0.2 },
    h4: { fontWeight: 800, letterSpacing: 0.3 },
    h5: { fontWeight: 800, letterSpacing: 0.3 },
    h6: { fontWeight: 800 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: 'var(--mui-palette-background-default)' },
        '*:focus-visible': { outline: 'none' },
      },
    },
    MuiButton: {
      defaultProps: { variant: 'contained', disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 10,
          minHeight: 40,
          lineHeight: 1.1,
          transition: 'transform 80ms ease, box-shadow 120ms ease, background-color 120ms ease',
          '&:active': { transform: 'translateY(1px)' },
          '&.Mui-focusVisible': { boxShadow: FOCUS_RING },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
          // Plain scheme paper -- no top-edge accent strip (owner ruling
          // 2026-07-14: "tell-tale AI implementation"). Side accents (e.g.
          // the nav rail's active bar) are unaffected by this rule.
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: { root: { fontWeight: 800, fontSize: '1.3rem', paddingBottom: 8 } },
    },
    MuiDialogActions: {
      styleOverrides: { root: { padding: '16px 24px', gap: 8 } },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--mui-palette-primary-main)',
          },
        },
        input: { fontSize: '1.05rem' },
      },
    },
    MuiInputLabel: {
      styleOverrides: { root: { fontSize: '1rem' } },
    },
  },
});

export default theme;
